import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Clock, AlertTriangle, Users, Loader2, Euro } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAvailableBarbers } from '@/hooks/useAvailableBarbers';
import { useBarberWaitTime } from '@/hooks/useBarberWaitTime';
import type { Barber, Service, Branch } from '@/types';

interface OutOfScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  barber: Barber;
  service: Service;
  branch: Branch;
  barberWorkEnd: string; // e.g., "20:00"
  estimatedServiceEnd: string; // e.g., "20:30"
  overtimeMinutes: number;
  onSelectAlternativeBarber: (barber: Barber) => void;
  onRequestOvertime: () => void;
  isRequestingOvertime?: boolean;
}

// Alternative barber card with wait time
function AlternativeBarberCard({
  barber,
  branchId,
  onSelect,
}: {
  barber: Barber;
  branchId: string;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const { totalWaitSeconds, waitingCount } = useBarberWaitTime({
    barberId: barber.barberId || barber.userId,
    branchId,
    enabled: true,
  });

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins} min`;
  };

  const getInitials = (name: string) => {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <button
      onClick={onSelect}
      className="flex items-center gap-3 p-3 bg-surface rounded-lg border border-hairline hover:border-accent/60 transition-colors w-full text-left"
    >
      {/* Avatar */}
      {barber.photoURL ? (
        <img
          src={barber.photoURL}
          alt={barber.displayName}
          className="w-12 h-12 rounded-full object-cover ring-1 ring-hairline"
        />
      ) : (
        <div className="w-12 h-12 rounded-full bg-surface-3 flex items-center justify-center text-ink-muted font-semibold">
          {getInitials(barber.displayName)}
        </div>
      )}

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-ink truncate">
          {barber.displayName}
        </p>
        <div className="flex items-center gap-2 text-sm">
          {waitingCount > 0 ? (
            <span className="text-ink-muted inline-flex items-center">
              <Clock className="w-3 h-3 inline mr-1 text-ink-subtle" />
              {formatTime(totalWaitSeconds)} ({waitingCount} {t('booking.outOfSchedule.inQueue')})
            </span>
          ) : (
            <span className="text-success font-medium">
              {t('booking.outOfSchedule.available')}
            </span>
          )}
        </div>
      </div>

      {/* Arrow */}
      <div className="text-ink-subtle">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );
}

export function OutOfScheduleModal({
  isOpen,
  onClose,
  barber,
  service,
  branch,
  barberWorkEnd,
  estimatedServiceEnd,
  overtimeMinutes,
  onSelectAlternativeBarber,
  onRequestOvertime,
  isRequestingOvertime = false,
}: OutOfScheduleModalProps) {
  const { t } = useTranslation();
  const { availableBarbers, loading: loadingBarbers } = useAvailableBarbers(branch.branchId);
  const [showAlternatives, setShowAlternatives] = useState(true);

  // Filter out the current barber and only show barbers within working hours
  const alternativeBarbers = availableBarbers.filter((b) => {
    // Exclude the selected barber
    if (b.barberId === barber.barberId) return false;

    // Check if this barber is still within working hours
    const now = new Date();
    const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = daysOfWeek[now.getDay()] as keyof typeof b.schedule;
    const daySchedule = b.schedule?.[today];

    if (!daySchedule?.isWorking || !daySchedule?.workEnd) return false;

    // Parse work end time
    const [endHour, endMinute] = daySchedule.workEnd.split(':').map(Number);
    const workEndTime = new Date();
    workEndTime.setHours(endHour, endMinute, 0, 0);

    // Check if current time + service duration would be within their working hours
    const serviceEndTime = new Date(now.getTime() + service.duration * 60000);

    return serviceEndTime <= workEndTime;
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[110] p-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="out-of-schedule-modal-title"
        className="bg-surface border border-hairline rounded-2xl max-w-lg w-full shadow-2xl max-h-[85vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 relative flex-shrink-0 border-b border-hairline">
          <button
            onClick={onClose}
            aria-label={t('common.close')}
            className="absolute top-4 right-4 text-ink-subtle hover:text-ink transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-5 h-5 text-warning shrink-0" aria-hidden="true" />
            <h2 id="out-of-schedule-modal-title" className="font-sans text-lg font-semibold text-ink">
              {t('booking.outOfSchedule.title')}
            </h2>
          </div>
          <p className="text-ink-muted text-sm">
            {t('booking.outOfSchedule.subtitle')}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {/* Warning message */}
          <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-ink font-medium mb-1">
                  {t('booking.outOfSchedule.scheduleConflict', {
                    barberName: barber.displayName,
                    workEnd: barberWorkEnd,
                  })}
                </p>
                <p className="text-ink-muted text-sm">
                  {t('booking.outOfSchedule.serviceWouldEnd', {
                    serviceEnd: estimatedServiceEnd,
                    overtime: overtimeMinutes,
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Alternative barbers section */}
          {alternativeBarbers.length > 0 && (
            <div className="mb-6">
              <button
                onClick={() => setShowAlternatives(!showAlternatives)}
                className="flex items-center justify-between w-full mb-3"
              >
                <h3 className="font-semibold text-ink flex items-center gap-2">
                  <Users className="w-5 h-5 text-ink-subtle" aria-hidden="true" />
                  {t('booking.outOfSchedule.alternativeBarbers')}
                </h3>
                <svg
                  className={`w-5 h-5 text-ink-subtle transition-transform ${showAlternatives ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showAlternatives && (
                <div className="space-y-2">
                  {loadingBarbers ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin text-ink-subtle" />
                    </div>
                  ) : (
                    alternativeBarbers.map((altBarber) => (
                      <AlternativeBarberCard
                        key={altBarber.barberId}
                        barber={altBarber}
                        branchId={branch.branchId}
                        onSelect={() => onSelectAlternativeBarber(altBarber)}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {alternativeBarbers.length === 0 && !loadingBarbers && (
            <div className="bg-surface-2 border border-hairline rounded-lg p-4 mb-6 text-center">
              <p className="text-ink-muted text-sm">
                {t('booking.outOfSchedule.noAlternatives')}
              </p>
            </div>
          )}

          {/* Request overtime section */}
          <div className="border-t border-hairline pt-6">
            <h3 className="font-semibold text-ink mb-3">
              {t('booking.outOfSchedule.stillWantThisBarber')}
            </h3>
            <p className="text-ink-muted text-sm mb-4">
              {t('booking.outOfSchedule.requestOvertimeExplanation', {
                barberName: barber.displayName,
              })}
            </p>

            <div className="bg-surface-2 border border-hairline rounded-lg p-4 mb-4">
              <p className="text-ink-muted text-sm">
                <strong className="text-ink font-medium">{t('booking.outOfSchedule.howItWorks')}</strong>
                <br />
                {t('booking.outOfSchedule.howItWorksDescription')}
              </p>
            </div>

            <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 mb-4 flex items-start gap-3">
              <Euro className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-ink text-sm font-medium">
                {t('booking.outOfSchedule.surchargeWarning')}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-hairline p-4 bg-surface-2 flex-shrink-0" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="ghost"
              onClick={onClose}
              className="flex-1"
              disabled={isRequestingOvertime}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={onRequestOvertime}
              isLoading={isRequestingOvertime}
              disabled={isRequestingOvertime}
              className="flex-1"
            >
              {isRequestingOvertime
                ? t('booking.outOfSchedule.requesting')
                : t('booking.outOfSchedule.requestOvertime')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
